import { useState } from 'react';
import { Link, Typography } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ConfirmDialog from '../ui/ConfirmDialog';

interface What3WordsLinkProps {
  address: string;
  /** Whether to show a confirmation dialog before opening the link. Default: true */
  requireConfirmation?: boolean;
  /** Typography variant for the address text. Default: 'body2' */
  variant?: 'body2' | 'caption';
  /** Test ID for the link element */
  testId?: string;
}

/**
 * A reusable link component for What3Words addresses.
 * Shows the location icon followed by the address text.
 * Optionally shows a confirmation dialog before opening the external link.
 */
export function What3WordsLink({
  address,
  requireConfirmation = true,
  variant = 'body2',
  testId = 'what3words-link',
}: What3WordsLinkProps) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const getWhat3WordsUrl = (addr: string) => {
    // Remove the /// prefix if present
    const cleanAddress = addr.replace(/^\/+/, '');
    return `https://what3words.com/${cleanAddress}`;
  };

  const url = getWhat3WordsUrl(address);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (requireConfirmation) {
      setConfirmDialogOpen(true);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleConfirm = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setConfirmDialogOpen(false);
  };

  const handleCancel = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <>
      <Link
        component="button"
        onClick={handleClick}
        data-testid={testId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: 'primary.main',
          cursor: 'pointer',
          border: 'none',
          background: 'none',
          padding: 0,
          textAlign: 'left',
        }}
      >
        <LocationOnIcon sx={{ fontSize: 16 }} />
        <Typography variant={variant}>{address}</Typography>
      </Link>

      {requireConfirmation && (
        <ConfirmDialog
          open={confirmDialogOpen}
          title="Open External Location"
          message="This will open What3Words in a new tab. Do you want to continue?"
          confirmText="Open"
          cancelText="Cancel"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </>
  );
}
